import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AuthAudience, PARTNER_ROUTES } from '@repo/common';
import { Role } from '@repo/frontend-shared';
import { SDSToastViewport } from '@repo/sds/SDSToast';
import { ResourceNotFound } from '@repo/ui/components/elements/ResourceNotFound';
import { FeatureFlagLoader } from '@repo/ui/components/FeatureFlagLoader/FeatureFlagLoader';
import { RequireRole } from '@repo/ui/components/route-guards';
import { ReactRouterSDSProvider } from '@repo/ui/components/SDS/ReactRouterSDSProvider';
import { CurrentUserContextProvider } from '@repo/ui/contexts/current-user-context';
import { TimezoneProvider } from '@repo/ui/contexts/timezone-provider';
import { I18nLocaleBootstrap } from '@repo/ui/i18n/i18n-locale-bootstrap';

import { PartnerLayout } from './components/partner-layout';

const LoginPage = lazy(() => import('./pages/login-page').then(m => ({ default: m.LoginPage })));
const ForgotPasswordPage = lazy(() =>
  import('./pages/forgot-password-page').then(m => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./pages/reset-password-page').then(m => ({ default: m.ResetPasswordPage })),
);
const DashboardPage = lazy(() => import('./pages/dashboard-page').then(m => ({ default: m.DashboardPage })));
const MembersPage = lazy(() => import('./pages/members-page').then(m => ({ default: m.MembersPage })));
const CohortsPage = lazy(() => import('./pages/cohorts-page').then(m => ({ default: m.CohortsPage })));
const CohortDetailPage = lazy(() => import('./pages/cohort-detail-page').then(m => ({ default: m.CohortDetailPage })));
const LicensePage = lazy(() => import('./pages/license/license-page').then(m => ({ default: m.LicensePage })));
const BillingPage = lazy(() => import('./pages/billing/billing-page').then(m => ({ default: m.BillingPage })));
const AnalyticsPage = lazy(() => import('./pages/analytics/analytics-page').then(m => ({ default: m.AnalyticsPage })));
const SettingsPage = lazy(() => import('./pages/settings-page').then(m => ({ default: m.SettingsPage })));

const PARTNER_ALLOWED_ROLES = [Role.PARTNER_MANAGER, Role.ADMIN, Role.SUPER_ADMIN] as const;

function AppRoutes() {
  return (
    <Routes>
      <Route path={PARTNER_ROUTES.PARTNER_LOGIN} element={<LoginPage />} />
      <Route path={PARTNER_ROUTES.PARTNER_FORGOTPASSWORD} element={<ForgotPasswordPage />} />
      <Route path={PARTNER_ROUTES.PARTNER_RESETPASSWORD} element={<ResetPasswordPage />} />
      <Route path={`${PARTNER_ROUTES.PARTNER_RESETPASSWORD}/:token`} element={<ResetPasswordPage />} />

      <Route
        path="/*"
        element={
          <RequireRole
            loginRoute={PARTNER_ROUTES.PARTNER_LOGIN}
            roles={PARTNER_ALLOWED_ROLES}
            audience={AuthAudience.Partner}
            preserveRedirect
          >
            <PartnerLayout>
              <Routes>
                <Route path="/" element={<Navigate to={PARTNER_ROUTES.PARTNER_DASHBOARD} replace />} />
                <Route path={PARTNER_ROUTES.PARTNER_DASHBOARD} element={<DashboardPage />} />
                <Route path={PARTNER_ROUTES.PARTNER_MEMBERS} element={<MembersPage />} />
                <Route path={PARTNER_ROUTES.PARTNER_COHORTS} element={<CohortsPage />} />
                <Route path={PARTNER_ROUTES.PARTNER_COHORT_DETAIL_PATTERN} element={<CohortDetailPage />} />
                <Route path={PARTNER_ROUTES.PARTNER_LICENSE} element={<LicensePage />} />
                <Route path={PARTNER_ROUTES.PARTNER_BILLING} element={<BillingPage />} />
                <Route path={PARTNER_ROUTES.PARTNER_ANALYTICS} element={<AnalyticsPage />} />
                <Route path={PARTNER_ROUTES.PARTNER_SETTINGS} element={<SettingsPage />} />
                <Route
                  path="*"
                  element={
                    <ResourceNotFound
                      resourceType="Page"
                      backRoute={PARTNER_ROUTES.PARTNER_DASHBOARD}
                      backButtonText="Back to Dashboard"
                    />
                  }
                />
              </Routes>
            </PartnerLayout>
          </RequireRole>
        }
      />
    </Routes>
  );
}

export function App() {
  return (
    <ReactRouterSDSProvider>
      <TimezoneProvider>
        <CurrentUserContextProvider apiUri={__API_URI__} logoutRoute={PARTNER_ROUTES.PARTNER_LOGIN}>
          <FeatureFlagLoader>
            <I18nLocaleBootstrap>
              <SDSToastViewport />
              <AppRoutes />
            </I18nLocaleBootstrap>
          </FeatureFlagLoader>
        </CurrentUserContextProvider>
      </TimezoneProvider>
    </ReactRouterSDSProvider>
  );
}
